import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type DateRangeViewSetting,
  FormattingOperator,
  FormattingRuleOperator,
  RelativeDatePeriod,
  RelativeDateUnit,
  RelativeDateValue as RelativeDateValueType,
} from '@ardoq/api-types';
import { currentDate, formatDateISO } from '@ardoq/date-time';
import { FlexBox } from '@ardoq/layout';
import { Select } from '@ardoq/select';
import { FIELD_SELECTOR, FormSize } from '@ardoq/forms';
import styled from 'styled-components';
import {
  DateBetweenPicker,
  RelativeDatePicker,
  RelativeDatePickerMode,
  RelativeDatePresetMode,
} from '@ardoq/date-time-picker';
import { RelativeDateValue as RelativeDateValueComponent } from '@ardoq/query-builder';
import { zIndex } from '@ardoq/design-tokens';
import { getDropdownMenuPortalTarget } from '@ardoq/dropdown-menu';
import { getConditionOptions } from './consts';
import { type TimelineDateRangeContentProps } from './timelineDateRangePropMappers';
import {
  getCondition,
  getSingleRelativePickerProps,
  isSingleRelativeCondition,
} from './utils';

// RelativeDateValue renders a FlexBox — we target its FIELD_SELECTOR children
// from outside since we own this layout context, not the shared component.
const RelativeDateValueRow = styled.div`
  & > div > ${FIELD_SELECTOR} {
    flex: 1;
    min-width: 0;
  }
`;

const portalHostStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: zIndex.MODAL_BACKDROP + 1,
};

const DEFAULT_RELATIVE_DATE_VALUE: RelativeDateValueType = {
  period: RelativeDatePeriod.THIS,
  unit: RelativeDateUnit.WEEK,
};

const getInitialDomainRangeForCondition = (
  newCondition: FormattingOperator
): DateRangeViewSetting => {
  if (newCondition === FormattingRuleOperator.RELATIVE_DATE) {
    return DEFAULT_RELATIVE_DATE_VALUE;
  }
  if (newCondition === FormattingRuleOperator.DATE_ON_OR_BEFORE) {
    return [null, formatDateISO(currentDate())];
  }
  if (newCondition === FormattingRuleOperator.DATE_ON_OR_AFTER) {
    return [formatDateISO(currentDate()), null];
  }
  return [null, null];
};

const TimelineDateRangeContent = ({
  domainRange,
  onDomainRangeChange,
  dateBetweenPickerProps,
  i18nLocale,
  renderInPortal = false,
}: TimelineDateRangeContentProps) => {
  const [condition, setCondition] = useState<FormattingOperator>(() =>
    getCondition(domainRange)
  );
  const [menuPortalTarget, setMenuPortalTarget] =
    useState<HTMLDivElement | null>(null);

  const handleConditionChange = (newCondition: FormattingOperator | null) => {
    if (!newCondition || newCondition === condition) return;
    setCondition(newCondition);
    onDomainRangeChange(getInitialDomainRangeForCondition(newCondition));
  };

  const singleRelativePickerProps = isSingleRelativeCondition(condition)
    ? getSingleRelativePickerProps(condition, domainRange, onDomainRangeChange)
    : null;

  return (
    <>
      {renderInPortal &&
        createPortal(
          <div ref={setMenuPortalTarget} style={portalHostStyle} />,
          getDropdownMenuPortalTarget()
        )}
      <FlexBox gap="small" flexDirection="column" width="full">
        <Select
          value={condition}
          options={getConditionOptions(i18nLocale)}
          onValueChange={handleConditionChange}
          menuPortalTarget={renderInPortal ? menuPortalTarget : undefined}
          i18nLocale={i18nLocale}
        />
        {condition === FormattingRuleOperator.RELATIVE_DATE && (
          <RelativeDateValueRow>
            <RelativeDateValueComponent
              value={domainRange as RelativeDateValueType}
              onValueChange={value => {
                if (value) onDomainRangeChange(value);
              }}
              menuPortalTarget={renderInPortal ? menuPortalTarget : undefined}
              i18nLocale={i18nLocale}
            />
          </RelativeDateValueRow>
        )}
        {condition === FormattingRuleOperator.DATE_BETWEEN && (
          <FlexBox gap="small">
            <DateBetweenPicker {...dateBetweenPickerProps} />
          </FlexBox>
        )}
        {singleRelativePickerProps && (
          <RelativeDatePicker
            {...singleRelativePickerProps}
            relativeDatePickerMode={RelativeDatePickerMode.DATE}
            presetMode={RelativeDatePresetMode.SHORT}
            formSize={FormSize.FULL}
            menuPortalTarget={renderInPortal ? menuPortalTarget : undefined}
          />
        )}
      </FlexBox>
    </>
  );
};

export { TimelineDateRangeContent };
